import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, resolveMediaUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ImageField } from "@/admin/MediaPicker";
import { SafeImg } from "@/components/site/Reveal";
import { useAssets, bustAssets, leaderPortrait } from "@/lib/assets";

const EMPTY = { name: "", slug: "", role: "", territory: "", bio: "", philosophy: "", portrait: "", gallery: [], videos: [], translations: {} };
const toLines = (a) => (a || []).join("\n");
const fromLines = (s) => (typeof s === "string" ? s.split("\n").map((x) => x.trim()).filter(Boolean) : s || []);

export default function AdminLeaders() {
  const isAdmin = useAuth().user?.role === "admin";

  const [leaders, setLeaders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [langTab, setLangTab] = useState("it");
  const assets = useAssets();

  const load = () => api.get("/leaders").then((r) => setLeaders(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setLangTab("it");
    setEditing({ ...EMPTY });
  };
  const openEdit = (l) => {
    setLangTab("it");
    setEditing({ ...l, translations: l.translations || {}, gallery: toLines(l.gallery), videos: toLines(l.videos) });
  };

  const save = async () => {
    setBusy(true);
    try {
      const translations = {};
      for (const lg of ["en", "es"]) {
        const c = { ...(editing.translations && editing.translations[lg] ? editing.translations[lg] : {}) };
        const has = Object.entries(c).some(([k, v]) => (Array.isArray(v) ? v.length : v && typeof v === "string"));
        if (has) translations[lg] = c;
      }
      const payload = { ...editing, translations, gallery: fromLines(editing.gallery), videos: fromLines(editing.videos) };
      delete payload.id;
      delete payload.created_at;
      if (editing.id) {
        await api.put(`/admin/leaders/${editing.id}`, payload);
        toast.success("Tour leader aggiornato");
      } else {
        await api.post("/admin/leaders", payload);
        toast.success("Tour leader creato");
      }
      bustAssets();
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (l) => {
    if (!window.confirm(`Eliminare "${l.name}"? Operazione definitiva.`)) return;
    try {
      await api.delete(`/admin/leaders/${l.id}`);
      toast.success("Tour leader eliminato");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setF = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const curT = (editing && editing.translations ? editing.translations[langTab] : null) || {};
  const setTL = (k, v) => setEditing((e) => ({ ...e, translations: { ...e.translations, [langTab]: { ...(e.translations?.[langTab] || {}), [k]: v } } }));

  return (
    <div data-testid="admin-leaders">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl">Tour Leader</h1>
          <p className="text-cream2 text-sm mt-1">Bio, filosofia di viaggio e materiali del team, in italiano, inglese e spagnolo.</p>
        </div>
        <button onClick={openNew} className="btn-copper !px-5 !py-2.5" data-testid="leaders-add-btn"><Plus className="h-4 w-4" /> Nuovo leader</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mt-8" data-testid="leaders-admin-grid">
        {leaders.map((l) => (
          <div key={l.id} className="card-dark overflow-hidden" data-testid={`leaders-card-${l.slug}`}>
            <SafeImg src={resolveMediaUrl(leaderPortrait(assets, l))} alt={l.name} className="w-full aspect-[4/3] object-cover" />
            <div className="p-4">
              <p className="font-serif text-xl text-cream">{l.name}</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-sand mt-1">{l.role}</p>
              <div className="flex gap-2 mt-4 justify-end">
                <button onClick={() => openEdit(l)} className="p-2 text-cream2 hover:text-copper" data-testid={`leaders-edit-${l.slug}`}><Pencil className="h-4 w-4" /></button>
                {isAdmin && (
                  <button onClick={() => remove(l)} className="p-2 text-cream2 hover:text-red-400" data-testid={`leaders-delete-${l.slug}`}><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[90] bg-obsidian/90 backdrop-blur-sm overflow-y-auto p-4 sm:p-8" data-testid="leaders-editor-modal">
          <div className="max-w-2xl mx-auto card-dark p-6 sm:p-8">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="font-serif text-2xl">{editing.id ? "Modifica leader" : "Nuovo leader"}</h2>
              <div className="flex items-center gap-2">
                {["it", "en", "es"].map((lg) => (
                  <button
                    key={lg}
                    onClick={() => setLangTab(lg)}
                    data-testid={`leaders-lang-tab-${lg}`}
                    className={"font-mono text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors " + (langTab === lg ? "border-copper text-copperlight" : "border-white/10 text-sand hover:text-cream")}
                  >
                    {lg}
                  </button>
                ))}
                <button onClick={() => setEditing(null)} className="text-sand hover:text-cream ml-2" data-testid="leaders-editor-close"><X className="h-5 w-5" /></button>
              </div>
            </div>

            {langTab === "it" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="label">Nome *</label><input className="field" value={editing.name} onChange={(e) => setF("name", e.target.value)} data-testid="leaders-field-name" /></div>
                <div><label className="label">Slug (URL) *</label><input className="field" placeholder="nome-cognome" value={editing.slug} onChange={(e) => setF("slug", e.target.value)} data-testid="leaders-field-slug" /></div>
                <div><label className="label">Ruolo</label><input className="field" value={editing.role} onChange={(e) => setF("role", e.target.value)} data-testid="leaders-field-role" /></div>
                <div><label className="label">Territori</label><input className="field" value={editing.territory} onChange={(e) => setF("territory", e.target.value)} data-testid="leaders-field-territory" /></div>
                <div className="sm:col-span-2"><label className="label">Bio</label><textarea className="field resize-none" rows="5" value={editing.bio} onChange={(e) => setF("bio", e.target.value)} data-testid="leaders-field-bio" /></div>
                <div className="sm:col-span-2"><label className="label">Filosofia di viaggio (citazione)</label><textarea className="field resize-none" rows="2" value={editing.philosophy} onChange={(e) => setF("philosophy", e.target.value)} data-testid="leaders-field-philosophy" /></div>
                <div className="sm:col-span-2">
                  <ImageField
                    label="Ritratto"
                    value={editing.portrait}
                    onChange={(url) => setF("portrait", url)}
                    testId="leaders-field-portrait"
                    hint={!editing.portrait && editing.slug && leaderPortrait(assets, editing) ? "Attualmente in uso: il ritratto destinato dal Media CMS. Scegline uno qui per sostituirlo." : "Scegli il ritratto tra le immagini già caricate nel Media CMS."}
                  />
                  {!editing.portrait && editing.slug && leaderPortrait(assets, editing) && (
                    <img src={resolveMediaUrl(leaderPortrait(assets, editing))} alt="" className="mt-2 h-16 w-16 object-cover border border-white/10" data-testid="leaders-portrait-current" />
                  )}
                </div>
                <div className="sm:col-span-2"><label className="label">Galleria (URL, uno per riga)</label><textarea className="field resize-none" rows="3" value={editing.gallery} onChange={(e) => setF("gallery", e.target.value)} data-testid="leaders-field-gallery" /></div>
                <div className="sm:col-span-2"><label className="label">Video (URL embed, uno per riga)</label><textarea className="field resize-none" rows="2" value={editing.videos} onChange={(e) => setF("videos", e.target.value)} data-testid="leaders-field-videos" /></div>
              </div>
            )}

            {langTab !== "it" && (
              <div className="space-y-4">
                <p className="font-mono text-[10px] uppercase tracking-widest text-sand">Traduzione {langTab.toUpperCase()} — lascia vuoto per usare il testo italiano.</p>
                <div><label className="label">Ruolo</label><input className="field" value={curT.role || ""} onChange={(e) => setTL("role", e.target.value)} data-testid={`leaders-tr-role-${langTab}`} /></div>
                <div><label className="label">Territori</label><input className="field" value={curT.territory || ""} onChange={(e) => setTL("territory", e.target.value)} data-testid={`leaders-tr-territory-${langTab}`} /></div>
                <div><label className="label">Bio</label><textarea className="field resize-none" rows="5" value={curT.bio || ""} onChange={(e) => setTL("bio", e.target.value)} data-testid={`leaders-tr-bio-${langTab}`} /></div>
                <div><label className="label">Filosofia di viaggio (citazione)</label><textarea className="field resize-none" rows="2" value={curT.philosophy || ""} onChange={(e) => setTL("philosophy", e.target.value)} data-testid={`leaders-tr-philosophy-${langTab}`} /></div>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-8">
              <button onClick={() => setEditing(null)} className="btn-ghost !px-5 !py-2.5" data-testid="leaders-cancel">Annulla</button>
              <button onClick={save} disabled={busy} className="btn-copper !px-6 !py-2.5 disabled:opacity-50" data-testid="leaders-save">{busy ? "Salvo…" : "Salva"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
